// Single-page monthly share card (PDF) for the monitoring section - a branded
// visual summary, not a data dump. Rendered with @react-pdf/renderer; replaces
// the old 1080×1350 canvas PNG (same payload, same download flow).
//
// ShareCardData: { monthLabel, kwh, savedRs, co2Kg, trees }
// (No peer percentile anywhere - that figure is a deterministic placeholder,
// and fake numbers never ship in user-facing artifacts.)

import { Document, Image, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { KG_CO2_PER_TREE } from "../constants";
import { COLORS, BODY_FONT, DISPLAY_FONT } from "./theme";

const inIN = (n) => n.toLocaleString("en-IN");
const logoSrc = () => `${process.env.PUBLIC_URL || ""}/greentek-logo.png`;

const s = StyleSheet.create({
  page: { backgroundColor: COLORS.parchment, fontFamily: BODY_FONT, color: COLORS.ink },

  band: { backgroundColor: COLORS.ink, paddingVertical: 30, paddingHorizontal: 56 },
  brandRow: { flexDirection: "row", alignItems: "center" },
  logo: { width: 76, height: 40.38, objectFit: "contain" },
  bandTitle: { marginTop: 14, fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 22, color: COLORS.white },

  body: { paddingHorizontal: 56, paddingTop: 64, flex: 1 },
  bigKwh: { fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 62, color: COLORS.ink },
  bigKwhUnit: { fontSize: 24, color: COLORS.muted },
  bigSub: { marginTop: 8, fontSize: 12, color: COLORS.muted },
  accentBar: {
    marginTop: 26,
    width: 120,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: COLORS.gold,
  },

  rows: { marginTop: 40 },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.hairline,
    paddingVertical: 18,
  },
  rowLabel: { fontSize: 11, color: COLORS.muted },
  rowValue: { fontSize: 15, fontWeight: 700 },

  footer: { paddingHorizontal: 56, paddingBottom: 44, alignItems: "center" },
  footerSite: { fontSize: 9.5, color: COLORS.muted },
  footerCompany: { marginTop: 5, fontSize: 8.5, color: COLORS.muted },
});

function CardRow({ label, value, color }) {
  return (
    <View style={s.row}>
      <Text style={s.rowLabel}>{label}</Text>
      <Text style={[s.rowValue, { color }]}>{value}</Text>
    </View>
  );
}

export function ShareCardDocument({ data }) {
  const d = data;
  return (
    <Document title={`Solar Summary · ${d.monthLabel}`} author="Greentek India Limited">
      <Page size="A4" style={s.page}>
        <View style={s.band}>
          <View style={s.brandRow}>
            <Image src={logoSrc()} style={s.logo} />
          </View>
          <Text style={s.bandTitle}>{`Solar Report · ${d.monthLabel}`}</Text>
        </View>

        <View style={s.body}>
          <Text style={s.bigKwh}>
            {inIN(d.kwh)}
            <Text style={s.bigKwhUnit}> kWh</Text>
          </Text>
          <Text style={s.bigSub}>generated this month</Text>
          <View style={s.accentBar} />

          <View style={s.rows}>
            <CardRow label="Money saved" value={`₹${inIN(d.savedRs)}`} color={COLORS.teal} />
            <CardRow
              label="CO₂ avoided"
              value={`${inIN(d.co2Kg)} kg ≈ ${inIN(Math.round(d.co2Kg / KG_CO2_PER_TREE))} trees`}
              color={COLORS.ink}
            />
          </View>
        </View>

        <View style={s.footer}>
          <Text style={s.footerSite}>alliance.greentekindia.co.in</Text>
          <Text style={s.footerCompany}>Greentek India Limited</Text>
        </View>
      </Page>
    </Document>
  );
}
